import {Enum} from "@/enums/system";
import {BOHQ} from "@/services/mdm/bohq";
import {BO} from "@/services/mdm/bo";
import {FieldProps, wdiCore, WdiModalAppResult} from "@/components/WdiControls";
import {EC} from "@/services/mdm/ec";
import {Product} from "@/services/mdm/product";
import {DataOptions} from "@/services/basic/metadata";
import {SoldTo} from "@/services/mdm/soldto";
import {DeliveryLine} from "@/services/mdm/deliveryline";
import {SoldToGroup} from "@/services/mdm/soldtogroup";
import {ResponseStatus} from "@/utils/request";
import {ProductPolicy} from "@/services/policy/productpolicy";
import moment from "moment";
import {FileUploadUtility} from "@/components/WdiControls/WdiFileUpload";
import {OaForm} from "@/services/mdm/oaform";
import {Tooltip} from "antd";
import {CustType} from "@/services/mdm/custtype";

export type PolicyModuleProps = {
  //基础信息
  basicInformationFields: string[],
  //政策明细
  policyDetailFields: string[],
  isActivity: boolean,
  policyType: Enum.PolicyType,
  groupId: number,
}

export type PolicyFieldNames =
  'productIdsReach'
  | 'productIdsRebate'
  | 'productIdsReachEx'
  | 'productIdsRebateEx'
  | 'productIdsFg'

export type ViewEntityProps = {
  productPolicy?: any;
  productPolicyRateList?: any[];
  productPolicySoldToList?: any[];
  productPolicyTargetList?: any[];
}

// 获取预算归属下拉框数据
export const bohqDataLoading = (params: any) => {
  return BOHQ.search(params).then(
    (response) => {
      return response.data.map((item) => ({value: item.bohqId, label: item.bohqName}));
    },
  );
};

// 获取使用部门下拉框数据
export const boTreeDataLoading = (params: any) => {
  return BO.search(params).then((response) => {
    const data = response.data;
    const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
      textField: "boName",
      valueField: "boId",
      id: "boId",
      pId: "parentId",
      level: "boLevel",
    }, data);
    return {treeData, dict};
  })
};

// 获取客户分类门下拉框数据
export const custTypeDataLoading = (params: API.CustTypeSearchParam) => {
  return CustType.search(params).then((response) => {
    const data = response.data;
    const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
      textField: "codeOrName",
      valueField: "custTypeId",
      id: "custTypeId",
      pId: "parentId",
      level: "custTypeLevel",
    }, data);
    return {treeData, dict};
  })
};

// 获取费用项下拉框数据
export const ecTreeDataLoading = (params: API.EcItemSearchParam) => {
  return EC.searchEc3(Object.assign({
    isActive: true,
    toParentLevel: 1,
    pageTypeIds: String(Enum.PageType.POLICY_REBATE),
  }, params)).then((response) => {
    const data = response.data;
    const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
      textField: "ecName",
      valueField: "ecId",
      id: "ecId",
      pId: "parentId",
      level: "ecLevel",
      disabledLevel: [0, 1, 2],
    }, data, (node: any, item: any) => {
      node.title = (
        <Tooltip placement="topLeft" title={item.remark}><span>{node.title}</span></Tooltip>
      );
      return node;
    });
    return {treeData, dict};
  })
};

// 获取达量产品下拉框数据
export const productTreeDataLoading = (params?: any, paramDisableLevel?: number[]) => {
  let res = Product.searchNoRoot(Object.assign({isActive: true}, params))
  return res.then(
    (response) => {
      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
        {
          textField: 'codeOrName',
          valueField: 'productId',
          id: 'productId',
          pId: 'parentId',
          level: 'productLevel',
          disabledLevel: paramDisableLevel ?? []
        },
        response.data,
      );
      return {treeData, dict};
    },
  );
};

// 获取结案频率下拉框数据
export const settlementFrequencyDataLoading = (params: any) => {
  return DataOptions.search(Object.assign({typeGroup: 15}, params)).then(
    (response) => {
      return response.data.map((item) => ({value: item.value, label: item.name}));
    },
  );
};

// 获取支付方式下拉数据
export const paymentMethodDataLoading = (params: API.DataOptionsFilter) => {
  return DataOptions.search(Object.assign(params, {typeGroup: 5})).then(
    (response) => {
      return response.data.map((item) => ({value: item.value, label: item.name}));
    },
  );
};

// 获取配送路线下拉数据
export const deliverLineDataLoading = (policyModuleData?: PolicyModuleProps) => {
  return DeliveryLine.search({isActive: true}).then(
    (response) => {
      return response.data.map((item) => ({value: item.deliveryLineCode, label: item.deliveryLineName}));
    },
  );
};


export const soldToDataLoading = (params: any) => {
  return SoldTo.ajaxSearchAll(Object.assign({withPermissions: true}, params)).then(res => {
    return res.data.map(item => ({
      value: item.soldToCode,
      label: wdiCore.String.CodeOrName(item.soldToCode, item.soldToName)
    }))
  })
};
export const soldToGroupDataLoading = (params: any) => {
  return SoldToGroup.ajaxSearch(Object.assign({withPermissions: true}, params)).then(res => {
    return res.data.map(item => ({
      value: item.soldToGroupId,
      label: wdiCore.String.CodeOrName(item.soldToGroupCode, item.soldToGroupName)
    }))
  })
};


export const initPageContext = async (productPolicyId: number) => {
  const result: WdiModalAppResult = {
    code: ResponseStatus.UNKNOWN
  };
  const viewEntity: ViewEntityProps = {
    productPolicy: {},
    productPolicyRateList: [],
    productPolicySoldToList: [],
    productPolicyTargetList: []
  };
  if (!productPolicyId) {
    return result
  }

  const productPolicyRes = await ProductPolicy.getVo(productPolicyId);
  if (productPolicyRes.code != ResponseStatus.SUCCESS) {
    return result;
  }

  const productPolicySoldtosRes = await ProductPolicy.QueryProductPolicySoldTo(productPolicyId);
  if (productPolicySoldtosRes.code != ResponseStatus.SUCCESS) {
    return result;
  }
  const productPolicyTargetRes = await ProductPolicy.QueryProductPolicyTarget(productPolicyId);
  if (productPolicyTargetRes.code != ResponseStatus.SUCCESS) {
    return result;
  }
  const productPolicyRatesRes = await ProductPolicy.SearchProductPolicyRate(productPolicyId);
  if (productPolicyRatesRes.code != ResponseStatus.SUCCESS) {
    return result;
  }
  const productPolicyProducts = await ProductPolicy.SearchProductPolicyProduct({
    policyId: productPolicyId
  });
  if (productPolicyProducts.code != ResponseStatus.SUCCESS) {
    return result;
  }
  //productPolicy
  viewEntity.productPolicy = productPolicyRes.data

  //productPolicyRateList
  viewEntity.productPolicyRateList = productPolicyRatesRes.data
  if (!wdiCore.String.IsNullOrEmpty(viewEntity.productPolicy.formulaRebateOriginal)) {
    viewEntity.productPolicyRateList.push({
      rowNumber: viewEntity.productPolicyRateList.length + 1,
      conditionReach: '初始预提公式',
      formulaRebate: viewEntity.productPolicy.formulaRebateOriginal,
      isInit: true
    });
  }

  //soldTo
  viewEntity.productPolicySoldToList = productPolicySoldtosRes.data
  //target
  viewEntity.productPolicyTargetList = productPolicyTargetRes.data
  // //product
  viewEntity.productPolicy.productIdsReach = productPolicyProducts.data.filter(item => Enum.TermsProductTypeEnum.REACH == item.termsProductType).map(item => (
    {
      value: item.productId,
      label: item.productName
    }
  ))
  viewEntity.productPolicy.productIdsRebate = productPolicyProducts.data.filter(item => Enum.TermsProductTypeEnum.REBATE == item.termsProductType).map(item => (
    {
      value: item.productId,
      label: item.productName
    }
  ))
  viewEntity.productPolicy.productIdsReachEx = productPolicyProducts.data.filter(item => Enum.TermsProductTypeEnum.REACH_EX == item.termsProductType).map(item => (
    {
      value: item.productId,
      label: item.productName
    }
  ))
  viewEntity.productPolicy.productIdsRebateEx = productPolicyProducts.data.filter(item => Enum.TermsProductTypeEnum.REBATE_EX == item.termsProductType).map(item => (
    {
      value: item.productId,
      label: item.productName
    }
  ))
  viewEntity.productPolicy.custTypeIds = viewEntity.productPolicy.custTypeIds?.split(",").map((item:string) => wdiCore.Number.ToInt(item)).filter((item:number)=>item);



  await initViewEntity(viewEntity);
  const oaForm = (await OaForm.get(viewEntity.productPolicy.oaFormNo))?.data
  const oaProgramVo: API.OaProgramVo = {
    documentNumber: oaForm?.oaFormNo,
    note: oaForm?.oaFormName,
    url: oaForm?.url
  }
  const productPolicy = Object.assign(viewEntity.productPolicy, {
    productPolicyRateList: viewEntity.productPolicyRateList,
    rebateStandard: viewEntity.productPolicyRateList,
    productPolicySoldToList: viewEntity.productPolicySoldToList,
    productPolicyTargetList: viewEntity.productPolicyTargetList,
    oaForms: oaForm ? [oaProgramVo] : []
  })

  result.code = ResponseStatus.SUCCESS;
  result.data = productPolicy;
  return result;

}


const initViewEntity = async (viewEntity: any) => {
  const bohqRes = await BOHQ.get(viewEntity.productPolicy.bohqId);
  viewEntity.productPolicy.bohqName = bohqRes?.data?.bohqName


  viewEntity.productPolicy.boIds = viewEntity.productPolicy?.boIds?.split(",").map((item: string) => Number(item));
  viewEntity.productPolicy.ecIds = viewEntity.productPolicy?.ecIds?.split(",").map((item: string) => Number(item));
  viewEntity.productPolicy.relatedId = viewEntity.productPolicy?.relatedId?.split(",").map((item: string) => Number(item));
  viewEntity.productPolicy.excludeId = viewEntity.productPolicy?.excludeId?.split(",").map((item: string) => Number(item));


  viewEntity.productPolicy.policyDate = [moment(viewEntity.productPolicy.startDate), moment(viewEntity.productPolicy.endDate)];
  viewEntity.productPolicy.fperiodStr = viewEntity.productPolicy.fperiod
  viewEntity.productPolicy.fperiod = moment(viewEntity.productPolicy.fperiod, 'YYYYMM')
  viewEntity.productPolicy.policyDateStr = viewEntity.productPolicy.startDate + ' ~ ' + viewEntity.productPolicy.endDate
  viewEntity.productPolicy.fileInfo = FileUploadUtility.parse(viewEntity.productPolicy.fileName, viewEntity.productPolicy.filePath)
}


export const initHistoryPageContext = async (productPolicyHistoryId: number) => {
  const result: WdiModalAppResult = {
    code: ResponseStatus.UNKNOWN
  };
  const viewEntity: ViewEntityProps = {
    productPolicy: {},
    productPolicyRateList: [],
    productPolicySoldToList: [],
    productPolicyTargetList: []
  };
  if (!productPolicyHistoryId) {
    return result
  }

  const productPolicyRes = await ProductPolicy.getHistoryVo(productPolicyHistoryId);
  if (productPolicyRes.code != ResponseStatus.SUCCESS) {
    return result;
  }

  const productPolicySoldtosRes = await ProductPolicy.QueryProductPolicySoldToHistory(productPolicyHistoryId);
  if (productPolicySoldtosRes.code != ResponseStatus.SUCCESS) {
    return result;
  }
  const productPolicyTargetRes = await ProductPolicy.QueryProductPolicyTargetHistory(productPolicyHistoryId);
  if (productPolicyTargetRes.code != ResponseStatus.SUCCESS) {
    return result;
  }
  const productPolicyRatesRes = await ProductPolicy.SearchProductPolicyRateHistory(productPolicyHistoryId);
  if (productPolicyRatesRes.code != ResponseStatus.SUCCESS) {
    return result;
  }

  //productPolicy
  viewEntity.productPolicy = productPolicyRes.data

  //productPolicyRateList
  viewEntity.productPolicyRateList = productPolicyRatesRes.data
  if (!wdiCore.String.IsNullOrEmpty(viewEntity.productPolicy.formulaRebateOriginal)) {
    viewEntity.productPolicyRateList.push({
      rowNumber: viewEntity.productPolicyRateList.length + 1,
      conditionReach: '初始预提公式',
      formulaRebate: viewEntity.productPolicy.formulaRebateOriginal,
      isInit: true
    });
  }
  //soldTo
  viewEntity.productPolicySoldToList = productPolicySoldtosRes.data
  //target
  viewEntity.productPolicyTargetList = productPolicyTargetRes.data
  await initViewEntity(viewEntity);
  const oaForm = (await OaForm.get(viewEntity.productPolicy.oaFormNo))?.data
  const oaProgramVo: API.OaProgramVo = {
    documentNumber: oaForm?.oaFormNo,
    note: oaForm?.oaFormName,
    url: oaForm?.url
  }
  const productPolicy = Object.assign(viewEntity.productPolicy, {
    productPolicyRateList: viewEntity.productPolicyRateList,
    rebateStandard: viewEntity.productPolicyRateList,
    productPolicySoldToList: viewEntity.productPolicySoldToList,
    productPolicyTargetList: viewEntity.productPolicyTargetList,
    oaForms: oaForm ? [oaProgramVo] : []
  })

  result.code = ResponseStatus.SUCCESS;
  result.data = productPolicy;
  return result;

}


export const initApplyPageContext = async (policyModuleData: PolicyModuleProps, data: any) => {
  let basicObj: any = {
    isCalcRebate: data.isCalcRebate,
    quantityMin: 0,
    policyType: data.policyType,
    policyTypeName: data.policyTypeName,
    programId: data.programId,
    programCodeOrName: data.programCodeOrName
  };

  //发货预提 君币核销 设置为按月结案
  if ([Enum.PolicyType.AUTO_CALC_BY_DELIVERY, Enum.PolicyType.AUTO_CALC_BY_JUNBI].includes(policyModuleData.policyType)) {
    basicObj = Object.assign(basicObj, {
      targetType: Enum.PolicyTargetType.FIXED_QUANTITY,
    });
  }

  //需申请 默认值设为整体结案
  if (policyModuleData.isActivity) {
    basicObj = Object.assign(basicObj, {});
  }

  return basicObj;

}

export const defaultPolicySoldToDateArr = ["1900-01-01", "2999-01-01"];
